import { httpsCallable, type HttpsCallable } from 'firebase/functions';
import { z } from 'zod';

import { functions } from '../firebase.js';
import { SchemaValidationFailedError } from '@kilat/shared';

/**
 * Sprint 86 — typed wrappers around the self-service payout-details
 * callables. Server gates on the matching custom claim and re-validates
 * with the same Zod shape; client validation here is fast feedback only.
 */

export const UpdatePayoutDetailsInputSchema = z.object({
  bankName: z.string().max(80).optional(),
  accountName: z.string().max(120).optional(),
  accountNumber: z.string().max(32).optional(),
  duitNowId: z.string().max(40).optional(),
  thresholdSen: z.number().int().nonnegative().max(1_000_000),
});
export type UpdatePayoutDetailsInput = z.infer<typeof UpdatePayoutDetailsInputSchema>;

export const UpdatePayoutDetailsOutputSchema = z.object({
  ok: z.literal(true),
});
export type UpdatePayoutDetailsOutput = z.infer<typeof UpdatePayoutDetailsOutputSchema>;

// ── agent ──────────────────────────────────────────────────────────────

const agentFn: HttpsCallable<UpdatePayoutDetailsInput, UpdatePayoutDetailsOutput> = httpsCallable(
  functions,
  'updateAgentPayoutDetails',
);

export async function callUpdateAgentPayoutDetails(
  input: UpdatePayoutDetailsInput,
): Promise<UpdatePayoutDetailsOutput> {
  const parsed = UpdatePayoutDetailsInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('updateAgentPayoutDetails.input', parsed.error.flatten());
  }
  const result = await agentFn(parsed.data);
  const out = UpdatePayoutDetailsOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('updateAgentPayoutDetails.output', out.error.flatten());
  }
  return out.data;
}

// ── recruiter ──────────────────────────────────────────────────────────

const recruiterFn: HttpsCallable<UpdatePayoutDetailsInput, UpdatePayoutDetailsOutput> =
  httpsCallable(functions, 'updateRecruiterPayoutDetails');

export async function callUpdateRecruiterPayoutDetails(
  input: UpdatePayoutDetailsInput,
): Promise<UpdatePayoutDetailsOutput> {
  const parsed = UpdatePayoutDetailsInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError(
      'updateRecruiterPayoutDetails.input',
      parsed.error.flatten(),
    );
  }
  const result = await recruiterFn(parsed.data);
  const out = UpdatePayoutDetailsOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError(
      'updateRecruiterPayoutDetails.output',
      out.error.flatten(),
    );
  }
  return out.data;
}
